import { useAuth0 } from "@auth0/auth0-react";
import useUserProfile from "./useUserProfile";
import useUserPermissions from "./useUserPermissions";
import useUserPreferences from "./useUserPreferences";
import Context from "./context";
import Banned from "./Banned";

const AuthContext = ({ token, children }) => {
	const {
		isAuthenticated: auth0Authenticated,
		isLoading: auth0Loading,
		error: auth0Error,
		user: auth0User,
		loginWithRedirect,
		logout,
	} = useAuth0();

	const {
		profile,
		error: profileError,
		isLoading: profileLoading,
		// mutate: profileMutate,
	} = useUserProfile(auth0User?.sub, token);

	const { checkPermissions } = useUserPermissions(
		profile?.permissions,
		profile?.uwr
	);

	const {
		preferences,
		error: preferencesError,
		isLoading: preferencesLoading,
		checkPreference,
		setPreference,
		setPreferenceWithExpiry,
		checkPreferenceExpired,
	} = useUserPreferences(profile?.id, token);

	const user = {
		...(profile || {}),
		preferences: preferences || {},
		auth0: auth0User,
	};

	const isAuth0Pending = auth0Authenticated && !auth0User?.sub && !auth0Error;
	const isProfilePending =
		auth0Authenticated && profileLoading && !profileError;
	const isPreferencesPending =
		auth0Authenticated &&
		!!profile?.id &&
		preferencesLoading &&
		!preferencesError;
	const isLoading =
		auth0Loading ||
		isAuth0Pending ||
		profileLoading ||
		isProfilePending ||
		isPreferencesPending;
	const isAuthenticated = auth0Authenticated && !!profile?.id;

	const cleanStorage = () => {
		localStorage.setItem("returnTo", window.location.toString());
	};
	const logoutWithReturnTo = () => {
		cleanStorage();
		logout({
			logoutParams: {
				returnTo: LOGOUT_REDIRECT,
			},
		});
	};
	const loginWithReturnTo = () => {
		cleanStorage();
		loginWithRedirect({
			appState: { returnTo: window.location.toString() },
		});
	};

	// expose login/logout to api calls
	window.login_fn = loginWithReturnTo;
	window.logout_fn = logoutWithReturnTo;

	if (profileError?.message === "BANNED") {
		return <Banned logoutWithReturnTo={logoutWithReturnTo} />;
	}

	return (
		<Context.Provider
			value={{
				user,
				isAuthenticated,
				isLoading,
				error: auth0Error || profileError || preferencesError,
				login: loginWithReturnTo,
				logout: logoutWithReturnTo,
				checkPermissions,
				checkPreference,
				setPreference,
				setPreferenceWithExpiry,
				checkPreferenceExpired,
				token,
			}}
		>
			{children}
		</Context.Provider>
	);
};

export default AuthContext;
